"use client";

import React from "react";
import { DatePicker as AntDatePicker, ConfigProvider } from "antd";
import type { DatePickerProps as AntDatePickerProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import { CalendarOutlined } from "@ant-design/icons";
import "../styles/datepicker.css";

export interface DatePickerProps extends AntDatePickerProps {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;

  /**
   * Custom placeholder text
   */
  placeholder?: string;
}

export interface DateRangePickerProps
  extends React.ComponentProps<typeof AntDatePicker.RangePicker> {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;

  /**
   * Custom placeholder text
   */
  placeholder?: [string, string];
}

/**
 * DatePicker component - Ant Design DatePicker with OpenStore Orion theming
 *
 * This component provides FULL access to all Ant Design DatePicker props while
 * automatically applying the OpenStore Orion theme and Figma design specifications.
 *
 * Features:
 * - Figma design specifications (4px border radius, specific colors, typography)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design DatePicker features and props
 *
 * @example
 * ```tsx
 * // Basic date picker with Orion theming
 * <DatePicker placeholder="Select Date" />
 *
 * // All Ant Design DatePicker props work
 * <DatePicker
 *   placeholder="Choose date"
 *   format="YYYY-MM-DD"
 *   disabledDate={(current) => current && current < dayjs().startOf('day')}
 *   showTime={{ format: 'HH:mm' }}
 *   onChange={handleDateChange}
 *   onOpenChange={handleOpenChange}
 *   open={isOpen}
 *   disabled={isDisabled}
 *   allowClear
 *   autoFocus
 *   size="large"
 *   status="error"
 *   popupClassName="custom-popup"
 *   suffixIcon={<CustomIcon />}
 *   className="custom-date-picker"
 * />
 *
 * // Range picker
 * <DatePicker.RangePicker
 *   placeholder={['Start Date', 'End Date']}
 *   onChange={handleRangeChange}
 * />
 *
 * // With custom styling
 * <DatePicker
 *   placeholder="Select Date"
 *   className="shadow-lg border-orion-200"
 * />
 * ```
 */
export const DatePicker: React.FC<DatePickerProps> = ({
  className,
  placeholder = "Select Date",
  ...props
}) => {
  return (
    <ConfigProvider theme={orionTheme}>
      <AntDatePicker
        className={clsx(
          "orion-date-picker",
          // Add custom classes for Tailwind overrides
          className
        )}
        placeholder={placeholder}
        suffixIcon={<CalendarOutlined className="text-gray-400" />}
        style={{
          borderRadius: "4px",
          borderColor: "#E6E6E6",
          fontFamily:
            'Inter, -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "1.3",
          color: "#999999",
          padding: "8px 12px",
          height: "40px",
          width: "100%",
        }}
        {...props}
      />
    </ConfigProvider>
  );
};

DatePicker.displayName = "DatePicker";

// Export convenience components for common patterns
export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  placeholder = ["Start Date", "End Date"],
  className,
  ...props
}) => (
  <ConfigProvider theme={orionTheme}>
    <AntDatePicker.RangePicker
      className={clsx("orion-date-picker", className)}
      placeholder={placeholder}
      suffixIcon={<CalendarOutlined className="text-gray-400" />}
      style={{
        borderRadius: "4px",
        borderColor: "#E6E6E6",
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.3",
        color: "#999999",
        padding: "8px 12px",
        height: "40px",
        width: "100%",
      }}
      {...props}
    />
  </ConfigProvider>
);

export const TimePicker: React.FC<
  Omit<DatePickerProps, "showTime"> & {
    showTime?: boolean | object;
  }
> = ({ showTime = true, className, ...props }) => (
  <ConfigProvider theme={orionTheme}>
    <AntDatePicker
      className={clsx("orion-date-picker", className)}
      showTime={showTime}
      suffixIcon={<CalendarOutlined className="text-gray-400" />}
      style={{
        borderRadius: "4px",
        borderColor: "#E6E6E6",
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.3",
        color: "#999999",
        padding: "8px 12px",
        height: "40px",
        width: "100%",
      }}
      {...props}
    />
  </ConfigProvider>
);

export const DateTimePicker: React.FC<DatePickerProps> = ({
  className,
  ...props
}) => (
  <ConfigProvider theme={orionTheme}>
    <AntDatePicker
      className={clsx("orion-date-picker", className)}
      showTime={{ format: "HH:mm" }}
      format="YYYY-MM-DD HH:mm"
      suffixIcon={<CalendarOutlined className="text-gray-400" />}
      style={{
        borderRadius: "4px",
        borderColor: "#E6E6E6",
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.3",
        color: "#999999",
        padding: "8px 12px",
        height: "40px",
        width: "100%",
      }}
      {...props}
    />
  </ConfigProvider>
);

// Re-export Ant Design DatePicker sub-components
export const RangePicker = AntDatePicker.RangePicker;
export const WeekPicker = AntDatePicker.WeekPicker;
export const MonthPicker = AntDatePicker.MonthPicker;
export const QuarterPicker = AntDatePicker.QuarterPicker;
export const YearPicker = AntDatePicker.YearPicker;
